import { AlertTriangle, CheckCircle } from "lucide-react";
import { InlineEditableSelect } from "../InlineEditableSelect";
import { type AuditSession } from "../../../../../models/audit/auditSession";

interface OutcomeSectionProps {
  isConclusive: boolean;
  outcome: string | null;
  attributeTestResult: AuditSession["sourceRows"][number]["attributeTestResults"][number];
  onUpdate: (
    field: string,
    value: string | number | object | boolean | null,
  ) => Promise<void>;
  disabled?: boolean;
}

export function OutcomeSection({
  isConclusive,
  outcome,
  attributeTestResult,
  onUpdate,
  disabled = false,
}: OutcomeSectionProps) {
  if (!isConclusive) {
    return null;
  }

  return (
    <div>
      <div className="text-sm text-font-secondary font-mono mb-2">Outcome</div>
      <InlineEditableSelect
        disabled={disabled}
        value={outcome || "fail"}
        options={[
          {
            value: "pass",
            label: "Pass",
            description: "No issues found",
          },
          {
            value: "fail",
            label: "Fail",
            description: "Issues found",
          },
          {
            value: "not_applicable",
            label: "Not Applicable",
            description: "Test does not apply",
          },
        ]}
        onSave={async (value) => {
          await onUpdate("outcome", value);
        }}
        renderDisplay={(value) => {
          const hasExceptions =
            attributeTestResult.isConclusive &&
            attributeTestResult.controlExceptions.length > 0;
          if (value === "pass") {
            if (hasExceptions) {
              return (
                <div className="bg-red-100 text-red-800 border border-red-200 p-2 rounded-md flex items-center gap-2">
                  <AlertTriangle className="h-6 w-6" />
                  Pass (with exceptions)
                </div>
              );
            }
            return (
              <div className="bg-green-100 text-green-800 border border-green-200 p-2 rounded-md flex items-center gap-2">
                <CheckCircle className="h-6 w-6" />
                No issues found
              </div>
            );
          } else if (value === "not_applicable") {
            return (
              <div className="bg-gray-100 text-gray-800 border border-gray-200 p-2 rounded-md flex items-center gap-2">
                <AlertTriangle className="h-6 w-6" />
                Not Applicable
              </div>
            );
          } else {
            return (
              <div className="bg-red-100 text-red-800 border border-red-200 p-2 rounded-md flex items-center gap-2">
                <AlertTriangle className="h-6 w-6" />
                Issues found
              </div>
            );
          }
        }}
      />
    </div>
  );
}
